import React, { useState } from 'react';

import { useGetTableEntries } from '../../../../shared/hooks/use-get-table-entries/use-get-table-entries.hook';
import { useGetTableResults } from '../../../../shared/hooks/use-get-table-results/use-get-table-results.hook';
import { useResetPageNumberAfterFiltering } from '../../../../shared/hooks/use-reset-page-number-after-filtering/use-reset-page-number-after-filtering.hook';
import { FirmwareOverviewModel } from '../../../../shared/models/firmware-overview.model';
import { getFirmwaresWithPagination } from '../../../../shared/thunks/get-firmwares-with-pagination/get-firmwares-with-pagination.thunk';
import { onChangeTableRowsPerPage } from '../../../../shared/utils/on-change-table-rows-per-page/on-change-table-rows-per-page.util';
import { onTablePageChange } from '../../../../shared/utils/on-table-page-change/on-table-page-change.util';
import { useAppDispatch } from '../../../../store/hooks';
import NoResults from '../../../shared/components/no-results/NoResults';
import Box from '../../../shared/components/ui/Box';
import SDCTable from '../../../shared/components/ui/Table/components/table/SDCTable';
import { getPaginationConfig } from '../../../shared/components/ui/Table/utils/get-pagination-config.util';
import { bulkActionHandler } from '../../../shared/utils/bulk-action-handler/bulk-action-handler.util';
import { FirmwareFilters } from '../../models/firmware-filters.model';
import { FirmwareHeaderAndPaginationFilters } from '../../models/firmware-header-and-pagination-filters.model';
import {
  addPagination,
  resetPagination,
} from '../../store/slices/firmware.slice';
import { getFirmwareTotalCount } from '../../thunk/get-firmware-total-count.thunk';
import { firmwareTableStyles } from '../../utils/firmware-table-styles.interpolation';
import { useGetFirmwareTableSelectors } from './hooks/use-get-firmware-table-selectors/use-get-firmware-table-selectors.hook';
import { useShowDeleteModalFirmwareTable } from './hooks/use-show-delete-modal/use-show-delete-modal-firmware-table.hook';
import { columns } from './utils/table-columns-def.util';

const FirmwareTable = (): JSX.Element => {
  const dispatch = useAppDispatch();

  const {
    firmwares: tableRows,
    isLoading,
    activeMandantId,
    firmwareFilters,
    page,
    rowsPerPage,
    count,
    firmwareHeaderFilters,
  } = useGetFirmwareTableSelectors();

  const [ids, setIds] = useState<number[]>([]);
  const nameFilter = firmwareFilters?.header?.name;

  useGetTableEntries<FirmwareFilters>(
    firmwareHeaderFilters,
    getFirmwareTotalCount,
    activeMandantId
  );
  useGetTableResults<
    FirmwareHeaderAndPaginationFilters,
    FirmwareOverviewModel[]
  >(firmwareFilters, getFirmwaresWithPagination, nameFilter, activeMandantId);

  const modalComponent = useShowDeleteModalFirmwareTable(ids, firmwareFilters);

  useResetPageNumberAfterFiltering(count, page, rowsPerPage, resetPagination);

  return (
    <>
      <Box
        sx={{
          height: '100%',
          padding: 0,
          '.table-footer': {
            minWidth: '1146px',
          },
        }}
        data-testid="firmware-table"
      >
        {tableRows?.length > 0 && (
          <SDCTable
            columns={columns}
            rows={tableRows}
            checkboxSelection={true}
            stickyHeader={true}
            bulkActions={true}
            bulkActionHandler={bulkActionHandler(setIds)}
            ariaLabel={'firmwares-table'}
            sx={{
              ...(firmwareTableStyles as object),
            }}
            paginationConfig={getPaginationConfig(
              count,
              rowsPerPage,
              page,
              onTablePageChange(dispatch, rowsPerPage, addPagination),
              onChangeTableRowsPerPage(dispatch, addPagination)
            )}
          />
        )}
        {tableRows?.length === 0 && !isLoading && <NoResults />}
      </Box>
      {modalComponent}
    </>
  );
};

export default FirmwareTable;
